import { Close } from "@mui/icons-material";
import { 
  Box, IconButton, Modal, Typography, TableContainer, Table, 
  TableHead, TableRow, TableCell, TableBody, Paper, List, ListItem 
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
import useSanitizeContent from "../../utils/useSanitizeContent";
import useThemeColor from "../../utils/useTheme";
import "../Extra/RenderHtml/RenderHtml.css";

const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};

const FAQModal = ({ open, onClose, htmlValue, programTitle }) => {
  const [value, setValue] = useState("");
  const styles = useThemeColor();

  const updateImageSources = useCallback(
    async (html, color) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      for (let img of images) {
        const fileName = img.getAttribute("src"); // Assuming the src contains the filename

        if (
          fileName &&
          !fileName.startsWith("http") &&
          !fileName.startsWith("data:")
        ) {
          try {
            const signedUrl = await generateSignedUrl(
              fileName,
              process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
            );

            // Replace filename with the signed URL in the img tag
            img.src = signedUrl;
          } catch (error) {
            console.error("Error generating signed URL:", error);
          }
        }

        // Performance optimizations
        if (!img.hasAttribute("loading")) {
          img.setAttribute("loading", "lazy");
        }
        if (!img.hasAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }
        if (!img.hasAttribute("referrerpolicy")) {
          img.setAttribute("referrerpolicy", "no-referrer");
        }

        // Preserve alignment from style, containerstyle, or data-text-align
        const imgStyle = img.getAttribute("style");
        const containerStyle = img.getAttribute("containerstyle");
        const dataAlign = img.getAttribute("data-text-align");

        img.style.display = "block";

        // Check containerstyle for margin-based alignment (from ImageResize extension)
        if (containerStyle) {
          // Parse margin values to determine alignment
          if (
            containerStyle.includes("margin: 0px auto 0px 0px") ||
            containerStyle.includes("margin:0px auto 0px 0px")
          ) {
            // Left aligned
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (
            containerStyle.includes("margin: 0px 0px 0px auto") ||
            containerStyle.includes("margin:0px 0px 0px auto")
          ) {
            // Right aligned
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          } else if (
            containerStyle.includes("margin: 0px auto") ||
            containerStyle.includes("margin:0px auto")
          ) {
            // Center aligned
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          }
          // Remove the custom attributes as we've converted them to standard styles
          img.removeAttribute("containerstyle");
          img.removeAttribute("wrapperstyle");
        } else if (imgStyle && imgStyle.includes("text-align")) {
          // Keep existing text-align in style
          const textAlign = imgStyle.match(
            /text-align:\s*(left|center|right)/
          )?.[1];
          if (textAlign === "left") {
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (textAlign === "center") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          } else if (textAlign === "right") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          }
        } else if (dataAlign) {
          // Convert data-text-align to margin styles
          if (dataAlign === "left") {
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (dataAlign === "center") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          } else if (dataAlign === "right") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          }
        } else {
          // Default center alignment
          img.style.marginLeft = "auto";
          img.style.marginRight = "auto";
        }

        if (!img.hasAttribute("width")) {
          img.setAttribute("width", "100%");
        }
        if (!img.hasAttribute("height")) {
          img.setAttribute("height", "auto");
        }

        // Wrap image in a responsive div for horizontal scrolling
        if (img.parentNode && !img.parentNode.classList.contains("img-responsive")) {
          const wrapper = doc.createElement("div");
          wrapper.className = "img-responsive";
          wrapper.style.overflowX = "auto";
          wrapper.style.width = "100%";
          wrapper.style.WebkitOverflowScrolling = "touch";
          
          img.parentNode.insertBefore(wrapper, img);
          wrapper.appendChild(img);
          
          img.style.maxWidth = "none";
          img.style.minWidth = "600px"; // Enforce a minimum width so it triggers scroll on small screens
        }
      }

      const links = Array.from(doc.querySelectorAll("a"));
      links.forEach((link) => {
        link.target = "_blank";
        let href = link.getAttribute("href");
        link.style.background = ""; // Clear background
        link.style.color = ""; //
        if (href) {
          if (href.startsWith("www.")) {
            link.setAttribute("href", `https://${href}`);
          } else if (!isValidUrl(href)) {
            // Optionally prepend a default protocol (http or https)
            link.setAttribute("href", `https://${href}`);
          }
        }
      });
      const paragraphs = [
        ...Array.from(doc.querySelectorAll("span")),
        ...Array.from(doc.querySelectorAll("strong")),
        ...Array.from(doc.querySelectorAll("em")),
        ...Array.from(doc.querySelectorAll("b")),
        ...Array.from(doc.querySelectorAll("ul")),
        ...Array.from(
          doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"])
        )
      ];
      paragraphs.forEach((p) => {
        p.style.background = ""; // Clear background
        p.style.color = color; // Clear color
      });

      const allParas = Array.from(doc.querySelectorAll("p"));
      let consecutiveJustifyBrs = []; // Array to track sequences of <p> elements

      allParas.forEach((p, index) => {
        // Check if it's a target <p> element
        if (
          p.classList.contains("ql-align-justify") &&
          p.innerHTML.trim() === "<br>"
        ) {
          consecutiveJustifyBrs.push(p);
        } else {
          // If the sequence ends, check its length and remove if needed
          if (consecutiveJustifyBrs.length > 7) {
            const lastFourElements = consecutiveJustifyBrs.slice(-4);
            lastFourElements.forEach((element) => element.remove());
          }
          // Reset the sequence tracking array
          consecutiveJustifyBrs = [];
        }
        p.style.color = color;
      });

      // Handle any remaining sequence at the end of the loop
      if (consecutiveJustifyBrs.length > 7) {
        const lastFourElements = consecutiveJustifyBrs.slice(-4);
        lastFourElements.forEach((element) => element.remove());
      }
      
      // Wrap all tables in a responsive div to enable horizontal scrolling
      const tables = Array.from(doc.querySelectorAll("table"));
      tables.forEach((table) => {
        if (!table.parentNode.classList.contains("table-responsive")) {
          const wrapper = doc.createElement("div");
          wrapper.className = "table-responsive";
          wrapper.style.overflowX = "auto";
          wrapper.style.width = "100%";
          wrapper.style.maxWidth = "100vw";
          wrapper.style.WebkitOverflowScrolling = "touch";
          wrapper.style.marginBottom = "1em";
          
          table.parentNode.insertBefore(wrapper, table);
          wrapper.appendChild(table);
          
          table.style.width = "100%";
          table.style.minWidth = "max-content"; // Force it to not squish text
        }
      });

      // Set the updated HTML back to the editor
      setValue(doc.body.innerHTML);
    },
    [setValue]
  );

  useEffect(() => {
    const initialHtml = htmlValue || "<p>No content available.</p>";
    updateImageSources(initialHtml, styles.color);
  }, [updateImageSources, htmlValue]);

  const sanitizedContent = useSanitizeContent(value);

  return (
    <Modal
      open={open}
      disableAutoFocus
      disableEnforceFocus
      onClose={onClose}
      aria-labelledby="meal-modal-title"
      aria-describedby="meal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          overflow: "auto",
          height: { xs: "80%" },
          width: { md: "95%", xs: "95%" },
          borderRadius: 2,
          transform: "translate(-50%, -50%)"
        }}
      >
        <Box
          sx={{
            padding: { xs: "28px", md: "10px" },
            position: "relative",
            bgcolor: styles.background,
            borderRadius: "16px 16px 0 0",
            boxShadow: 24,
            overflow: "hidden" // Ensures pseudo-element stays within the box
          }}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <Box
            display="flex"
            justifyContent="end"
            alignItems="flex-end"
            width="100%"
          >
            <IconButton
              sx={{
                svg: {
                  fill: styles.color
                }
              }}
              onClick={onClose}
            >
              <Close />
            </IconButton>
          </Box>
          <Box
            sx={{ padding: { sm: 0, md: "10px 25px" }, width: "100%", overflowX: "hidden" }}
            display={"flex"}
            gap="10px"
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {!(programTitle?.toLowerCase()?.includes("lazy")) && (sanitizedContent || sanitizedContent !== "undefined") ? (
              <div
                className="preview-html"
                style={{
                  marginTop: "10px",
                  width: "100%",
                  overflowX: "hidden",
                  ".ql-align-center": {
                    display: "flex",
                    justifyContent: "center"
                  },
                  p: {
                    color: `${styles.color} !important`
                  },
                  h1: {
                    color: `${styles.color} !important`
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizedContent ? sanitizedContent : <div />
                }}
              />
            ) : (
              <></>
            )}
            {programTitle?.toLowerCase()?.includes("lazy") && (
              <Box sx={{ mt: 3, width: "100%", textAlign: "left" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: styles.color,
                      fontSize: { xs: "14px", sm: "15px" },
                      pointerEvents: "none",
                      mt: 3,
                      mb: 1.5,
                      fontWeight: "bold"
                    }}
                  >
                    Determining Protein Grams:
                  </Typography>
                  <Typography
                    variant="body3"
                    sx={{
                      color: styles.color,
                      fontSize: { xs: "13px", sm: "15px" },
                      pointerEvents: "none",
                      display: "block",
                      mb: 1.5
                    }}
                  >
                    Use nutrition labels when available.
                  </Typography>
                  <Typography
                    variant="body3"
                    sx={{
                      color: styles.color,
                      fontSize: { xs: "13px", sm: "15px" },
                      pointerEvents: "none",
                      display: "block",
                      mb: 1.5
                    }}
                  >
                    Here&rsquo;s a cheat sheet when you don&rsquo;t have a
                    label:
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      my: 2,
                      boxShadow: "none",
                      width: "100%",
                      backgroundColor: "transparent",
                      overflowX: "auto"
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        width: "100%",
                        minWidth: { xs: 300, sm: "100%" },
                        "& td, & th": {
                          backgroundColor: "transparent",
                          border: `1px solid ${styles.color}`,
                          color: styles.color,
                          fontSize: { xs: "12px", sm: "14px" },
                          padding: { xs: "6px 4px", sm: "8px" }
                        },
                        "& td:nth-of-type(1), & th:nth-of-type(1)": {
                          width: { xs: "48%", sm: "auto" }
                        },
                        "& td:nth-of-type(2), & th:nth-of-type(2)": {
                          width: { xs: "28%", sm: "auto" }
                        },
                        "& td:nth-of-type(3), & th:nth-of-type(3)": {
                          width: { xs: "24%", sm: "auto" }
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Serving Size</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Protein (g)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          {
                            item:
                              "Cooked Beef / Chicken / Turkey / Pork / Seafood",
                            serving: "1 oz (28 g)",
                            protein: "7"
                          },
                          {
                            item:
                              "Raw Beef / Chicken / Turkey / Pork / Seafood",
                            serving: "1 oz (28 g)",
                            protein: "5"
                          },
                          {
                            item: "Deli Meat",
                            serving: "1 oz (28 g)",
                            protein: "5"
                          },
                          {
                            item: "Cheese",
                            serving: "1 oz (28 g)",
                            protein: "7"
                          },
                          { item: "Milk", serving: "1 cup", protein: "8" },
                          { item: "Soy milk", serving: "1 cup", protein: "8" },
                          {
                            item: "Soy “meat” (TVP)",
                            serving: "1 oz (28 g)",
                            protein: "13"
                          },
                          {
                            item: "Tofu (firm)",
                            serving: "1 oz (28 g)",
                            protein: "5"
                          }
                        ].map((row) => (
                          <TableRow key={row.item}>
                            <TableCell>{row.item}</TableCell>
                            <TableCell align="center">{row.serving}</TableCell>
                            <TableCell align="center">{row.protein}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography
                    variant="h6"
                    sx={{
                      color: styles.color,
                      fontSize: { xs: "14px", sm: "15px" },
                      pointerEvents: "none",
                      mt: 3,
                      mb: 1.5,
                      fontWeight: "bold"
                    }}
                  >
                    When eating out:
                  </Typography>
                  <List sx={{ listStyle: "inside", py: 0 }}>
                    <ListItem sx={{ display: "list-item", py: 0.75, pl: 0, color: styles.color }}>
                      <Typography
                        variant="body3"
                        sx={{
                          color: styles.color,
                          fontSize: { xs: "13px", sm: "15px" },
                          pointerEvents: "none"
                        }}
                      >
                        Cooked protein servings are typically about the size
                        of your palm or a deck of cards. This is roughly 3.5
                        oz (100 g) and provides ~25 g of protein for meat or
                        seafood, or ~18 g of protein for tofu.
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0.75, pl: 0, color: styles.color }}>
                      <Typography
                        variant="body3"
                        sx={{
                          color: styles.color,
                          fontSize: { xs: "13px", sm: "15px" },
                          pointerEvents: "none"
                        }}
                      >
                        If the serving size is much larger or smaller,
                        estimate using the rule above.
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0.75, pl: 0, color: styles.color }}>
                      <Typography
                        variant="body3"
                        sx={{
                          color: styles.color,
                          fontSize: { xs: "13px", sm: "15px" },
                          pointerEvents: "none"
                        }}
                      >
                        Weights listed on menus are raw weights. Use the
                        table above, and reduce the listed weight by 25% if
                        it includes a bone.
                      </Typography>
                    </ListItem>
                  </List>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FAQModal;
